import { genericGETDatabaseCall, genericPOSTDatabaseCall, RenderOneTable, RenderProjectionTable, CapTable } from "../helperFunctions";
import { Rnd } from 'react-rnd';
import { useLocation } from "react-router";
import { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { Switch, Modal, ColorPicker, message, Tooltip as AntTooltip } from "antd";
import { SaveOutlined, ArrowLeftOutlined, EditOutlined, WarningOutlined, CloseOutlined,
    RiseOutlined, PieChartOutlined, FundOutlined, ThunderboltOutlined } from "@ant-design/icons"
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    LabelList,
    ResponsiveContainer
} from 'recharts';

import '../FinancialBuilder/financial-builder.css'
import '../CriticalFinance/CriticalFinanceTable.css'
import '../TemplateRender/TemplateRender.css'

/* --- per-widget customization helpers -------------------------------------
   Each widget's entry in widgetStates may carry style overrides alongside its
   position (x, y) and size (width, height). Empty / undefined means "use the
   theme default", so old saved layouts keep working unchanged. */
const col = (v, fallback) => (v === '' || v == null) ? fallback : v;
const px = (v, fallback) => (v === '' || v == null) ? fallback : (typeof v === 'number' ? v + 'px' : v);

/* Tables (entity-table) set font-size/color on th/td via CSS, which would
   override inline styles on the <table>. So we pass overrides as CSS variables
   on the container; the table's th/td read them (with the theme value as
   fallback, so untouched tables are unaffected). */
function tableContainerStyle(custom) {
    return {
        margin: '0px', height: '100%', overflow: 'auto',
        background: col(custom.bg, undefined),
        borderRadius: px(custom.radius, undefined),
        '--pn-tbl-fs': custom.fontSize ? Number(custom.fontSize) + 'px' : undefined,
        '--pn-tbl-fg': col(custom.fg, undefined),
        '--pn-tbl-hfs': custom.headerFontSize ? Number(custom.headerFontSize) + 'px' : undefined,
        '--pn-tbl-hfg': col(custom.headerFg, undefined),
    };
}

const FIELD_META = {
    bg: { label: 'Background color', kind: 'color', def: '#141b24' },
    fg: { label: 'Body text color', kind: 'color', def: '#e8edf2' },
    headerFg: { label: 'Header text color', kind: 'color', def: '#8a97a6' },
    headerFontSize: { label: 'Header font size (px)', kind: 'number' },
    accent: { label: 'Accent color', kind: 'color', def: '#3ec6c0' },
    titleSize: { label: 'Title font size (px)', kind: 'number' },
    valueSize: { label: 'Value font size (px)', kind: 'number' },
    fontSize: { label: 'Font size (px)', kind: 'number' },
    radius: { label: 'Corner radius (px)', kind: 'number' },
    width: { label: 'Width (px)', kind: 'number', placeholder: 'auto' },
    height: { label: 'Height (px)', kind: 'number', placeholder: 'auto' },
    showTag: { label: 'Show "Firm Wide" tag', kind: 'switch' },
    ringSize: { label: 'Gauge size (px)', kind: 'number' },
    chartSize: { label: 'Chart size (px)', kind: 'number' },
    barSize: { label: 'Bar width (px)', kind: 'number' },
    chartStyle: { label: 'Money series style', kind: 'select', options: ['Bar', 'Line'], def: 'Line' },
};

function fieldsFor(type) {
    switch (type) {
        case 'Big Number Card': return ['bg', 'fg', 'accent', 'valueSize', 'titleSize', 'ringSize', 'radius', 'showTag', 'width', 'height'];
        case 'Pie Chart': return ['bg', 'fg', 'titleSize', 'fontSize', 'chartSize', 'radius', 'width', 'height'];
        case 'Waterfall Chart': return ['bg', 'fg', 'titleSize', 'fontSize', 'barSize', 'radius', 'width', 'height'];
        case 'Chart (Projections)': return ['bg', 'fg', 'titleSize', 'fontSize', 'chartStyle', 'radius', 'width', 'height'];
        case 'Two Col Table':
        case 'Cap Table':
        case 'More Than Two Col Table': return ['bg', 'headerFg', 'headerFontSize', 'fg', 'fontSize', 'radius', 'width', 'height'];
        default: return ['bg', 'fg', 'fontSize', 'radius', 'width', 'height'];
    }
}

/* Shared color palettes (used by both the chart components and the edit modal so
   the per-series pickers preview the colors the chart actually renders). */
const PIE_PALETTE = ['#3ec6c0', '#5dade2', '#f39c12', '#e74c3c', '#9b59b6', '#1abc9c', '#f6c454', '#16a085'];
const BAR_PALETTE = ['#3ec6c0', '#5dade2', '#f6c454', '#fd6f7a'];
const LINE_PALETTE = ['#a78bfa', '#22d3ee', '#fb923c', '#c084fc'];
const WATERFALL_DEFAULTS = { total: '#3ec6c0', increase: '#27ae60', decrease: '#e74c3c' };

const CHART_TYPES = ['Chart (Projections)', 'Pie Chart', 'Waterfall Chart'];

/* Big Number Cards mirror the PSBB Dashboard's KPI cards (.pn-kpi): an accent
   chip + label header, a big value, then a small "firm-wide" footer. The metric
   data carries no icon, so pick one deterministically from the name so a given
   card keeps the same icon across renders. */
const CARD_ICONS = [<RiseOutlined />, <PieChartOutlined />, <FundOutlined />, <ThunderboltOutlined />];
function cardIconFor(name) {
    const s = String(name || '');
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % CARD_ICONS.length;
    return CARD_ICONS[h];
}

function defaultSizeFor(type) {
    switch (type) {
        case 'Chart (Projections)': return { width: 580, height: 400 };
        case 'Pie Chart': return { width: 420, height: 240 };
        case 'Waterfall Chart': return { width: 480, height: 320 };
        default: return { width: 'auto', height: 'auto' };
    }
}

// Is a projection row a percentage (line) series rather than a money (bar) series?
function isPercentRow(label, values) {
    return values.some((v) => String(v).includes('%')) ||
        label.toLowerCase().includes('%') ||
        label.toLowerCase().includes('percent');
}

// Default color the chart would use for each series, keyed by series label / role.
function defaultColorMap(metric, palette) {
    const map = {};
    const pal = palettesFor(palette);
    if (metric.data_type === 'Pie Chart') {
        metric.data.forEach((d, i) => { map[d.label] = pal.pie[i % pal.pie.length]; });
    } else if (metric.data_type === 'Chart (Projections)') {
        let m = 0, l = 0;
        metric.data.forEach((row) => {
            const label = row[0];
            if (isPercentRow(label, row.slice(1))) { map[label] = pal.line[l % pal.line.length]; l++; }
            else { map[label] = pal.bar[m % pal.bar.length]; m++; }
        });
    } else if (metric.data_type === 'Waterfall Chart') {
        Object.assign(map, WATERFALL_DEFAULTS);
    }
    return map;
}

// The per-series color rows to show in the edit modal: [{ key, label }].
function seriesColorFields(metric) {
    switch (metric.data_type) {
        case 'Pie Chart': return metric.data.map((d) => ({ key: d.label, label: d.label }));
        case 'Chart (Projections)': return metric.data.map((row) => ({ key: row[0], label: row[0] }));
        case 'Waterfall Chart': return [
            { key: 'total', label: 'Total bars' },
            { key: 'increase', label: 'Increase' },
            { key: 'decrease', label: 'Decrease' },
        ];
        default: return [];
    }
}

async function saveDashboardEntityToServer(entity) {
    return genericPOSTDatabaseCall('/api/psbb/addDashboardEntity', { dashboard: entity }, 'dashboard entity');
}

async function getDashboardEntityFromServer(templateID) {
    return genericGETDatabaseCall('/api/psbb/getDashboardEntity?sharedLinkUUID=' + templateID, 'dashboard entity');
}

export function TemplateRenderWithContext({ context }) {
    const { setPageNum, mergedData, messageApi, entitySelected } = useContext(context);

    return (
        <TemplateRenderBase
            setPageNum={setPageNum}
            mergedData={mergedData}
            messageApi={messageApi}
            withContext={true}
            entitySelected={entitySelected}
        />
    );
}

export default function TemplateRenderWithTemplateID() {
    const location = useLocation();
    const templateId = (new URLSearchParams(location.search)).get('templateId');
    const [messageApi, contextHolder] = message.useMessage();
    const [mergedData, setMergedData] = useState({
        'data': [],
        'layout': []
    });
    const [loading, setLoading] = useState(true);

    const getTemplateData = useCallback(async () => {
        messageApi.loading("Getting dashboard data...");
        try {
            const data = await getDashboardEntityFromServer(templateId);
            setMergedData(data);
            messageApi.destroy();
            messageApi.success("Loaded dashboard data");
            setLoading(false);
        } catch (err) {
            messageApi.destroy();
            messageApi.error("Unable to get dashboard data");
            console.error(err);
        }
    }, [messageApi, templateId]);

    useEffect(() => {
        getTemplateData();
    }, [getTemplateData])

    return (
        <div className="pn-render-page">
            {contextHolder}
            {!loading &&
                <TemplateRenderBase
                    mergedData={mergedData.data}
                    withContext={false}
                    initLayout={mergedData.layout}
                    dashboardNameFromParent={mergedData.info.name}
                    initCanvas={{ width: mergedData.info.canvasWidth, height: mergedData.info.canvasHeight }}
                    initTheme={mergedData.info.theme}
                />
            }
            {loading && <div className="pn-render-loading">Loading dashboard…</div>}
        </div>
    );
}


const CANVAS_DEFAULTS = { width: 1280, height: 2000 };
const CANVAS_MIN = { width: 480, height: 480 };

/* Preset dashboard themes. Each is a bundle of CSS-variable overrides applied
   to the canvas, so every widget that reads the theme tokens adopts it at once
   (without editing each widget). Per-widget overrides set in the edit modal are
   inline on the widget, so they still win over the theme. 'default' applies no
   overrides → follows the app's own dark/light tokens. */
const DARK_BASE = {
  '--pn-glass': 'rgba(255,255,255,0.05)',
  '--pn-glass-2': 'rgba(255,255,255,0.08)',
  '--pn-fg-primary': '#f3f5f8',
  '--pn-fg-secondary': '#c2cad6',
  '--pn-fg-tertiary': '#8b94a3',
  '--pn-fg-quaternary': '#646c7a',
  '--pn-border-default': 'rgba(255,255,255,0.09)',
  '--pn-border-strong': 'rgba(255,255,255,0.15)',
  '--pn-border-subtle': 'rgba(255,255,255,0.05)',
  '--pn-tint-08': 'rgba(255,255,255,0.08)',
  '--pn-tint-02': 'rgba(255,255,255,0.025)',
  '--pn-shadow-elevated': 'rgba(0,0,0,0.4) 0px 8px 24px',
};
const LIGHT_BASE = {
  '--pn-fg-primary': '#0e2841',
  '--pn-fg-secondary': '#3a4b5e',
  '--pn-fg-tertiary': '#6b7a8d',
  '--pn-fg-quaternary': '#8a93a3',
  '--pn-border-default': 'rgba(14,40,65,0.12)',
  '--pn-border-strong': 'rgba(14,40,65,0.20)',
  '--pn-border-subtle': 'rgba(14,40,65,0.07)',
  '--pn-tint-08': 'rgba(14,40,65,0.06)',
  '--pn-tint-02': 'rgba(14,40,65,0.03)',
  '--pn-shadow-elevated': 'rgba(14,40,65,0.10) 0px 8px 24px',
};
const THEMES = {
  default: { name: 'Default', vars: {}, palette: null },
  midnight: { name: 'Midnight', vars: { ...DARK_BASE, '--pn-bg-panel': '#0b1220', '--pn-glass': 'rgba(120,150,255,0.07)', '--pn-fg-primary': '#eaf0ff', '--pn-accent': '#5b8cff', '--pn-accent-dim': 'rgba(91,140,255,0.18)' }, palette: ['#5b8cff', '#8ab4ff', '#3ec6c0', '#a78bfa', '#22d3ee', '#6ee7ff', '#c084fc', '#f6c454'] },
  ocean: { name: 'Ocean', vars: { ...DARK_BASE, '--pn-bg-panel': '#071a1f', '--pn-glass': 'rgba(45,212,191,0.08)', '--pn-fg-primary': '#e6fbf7', '--pn-accent': '#2dd4bf', '--pn-accent-dim': 'rgba(45,212,191,0.18)' }, palette: ['#2dd4bf', '#22d3ee', '#38bdf8', '#5b8cff', '#a78bfa', '#f6c454', '#fb7185', '#34d399'] },
  forest: { name: 'Forest', vars: { ...DARK_BASE, '--pn-bg-panel': '#0c1612', '--pn-glass': 'rgba(52,211,153,0.07)', '--pn-fg-primary': '#e7f6ee', '--pn-accent': '#34d399', '--pn-accent-dim': 'rgba(52,211,153,0.18)' }, palette: ['#34d399', '#10b981', '#84cc16', '#22d3ee', '#f6c454', '#fb923c', '#5dade2', '#a78bfa'] },
  plum: { name: 'Plum', vars: { ...DARK_BASE, '--pn-bg-panel': '#160f1e', '--pn-glass': 'rgba(190,140,255,0.07)', '--pn-fg-primary': '#f3eafc', '--pn-accent': '#b78bff', '--pn-accent-dim': 'rgba(183,139,255,0.18)' }, palette: ['#b78bff', '#d8a7ff', '#7c8cff', '#22d3ee', '#f6c454', '#fd6f7a', '#5dade2', '#8ee6d0'] },
  ember: { name: 'Ember', vars: { ...DARK_BASE, '--pn-bg-panel': '#1a110b', '--pn-glass': 'rgba(251,146,60,0.07)', '--pn-fg-primary': '#fbeee2', '--pn-accent': '#fb923c', '--pn-accent-dim': 'rgba(251,146,60,0.18)' }, palette: ['#fb923c', '#f6c454', '#fd6f7a', '#f43f5e', '#a78bfa', '#22d3ee', '#34d399', '#fbbf24'] },
  rose: { name: 'Rose', vars: { ...DARK_BASE, '--pn-bg-panel': '#1b0f15', '--pn-glass': 'rgba(251,113,133,0.07)', '--pn-fg-primary': '#fceaef', '--pn-accent': '#fb7185', '--pn-accent-dim': 'rgba(251,113,133,0.18)' }, palette: ['#fb7185', '#f472b6', '#c084fc', '#fda4af', '#22d3ee', '#f6c454', '#5dade2', '#34d399'] },
  graphite: { name: 'Graphite', vars: { ...DARK_BASE, '--pn-bg-panel': '#16181c', '--pn-accent': '#c9cdd4', '--pn-accent-dim': 'rgba(201,205,212,0.16)' }, palette: ['#c9cdd4', '#9aa3af', '#6ee7d6', '#5dade2', '#f6c454', '#fd6f7a', '#a78bfa', '#7f8da3'] },
  paper: { name: 'Paper (light)', vars: { ...LIGHT_BASE, '--pn-bg-panel': '#ffffff', '--pn-glass': '#ffffff', '--pn-glass-2': '#f4f7fb', '--pn-accent': '#12c0bd', '--pn-accent-dim': 'rgba(18,192,189,0.14)' }, palette: ['#12c0bd', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#ec4899', '#0ea5e9'] },
  sky: { name: 'Sky (light)', vars: { ...LIGHT_BASE, '--pn-bg-panel': '#f1f6fd', '--pn-glass': '#ffffff', '--pn-glass-2': '#e8f1fb', '--pn-fg-primary': '#10243a', '--pn-accent': '#2563eb', '--pn-accent-dim': 'rgba(37,99,235,0.14)' }, palette: ['#2563eb', '#0ea5e9', '#0d9488', '#f59e0b', '#7c3aed', '#dc2626', '#16a34a', '#db2777'] },
  mint: { name: 'Mint (light)', vars: { ...LIGHT_BASE, '--pn-bg-panel': '#f1faf6', '--pn-glass': '#ffffff', '--pn-glass-2': '#e4f4ec', '--pn-fg-primary': '#0f2a20', '--pn-accent': '#10b981', '--pn-accent-dim': 'rgba(16,185,129,0.14)' }, palette: ['#10b981', '#0d9488', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9', '#ec4899'] },
  sand: { name: 'Sand (light)', vars: { ...LIGHT_BASE, '--pn-bg-panel': '#fbf7f0', '--pn-glass': '#fffdf9', '--pn-glass-2': '#f3ece0', '--pn-fg-primary': '#3a2f25', '--pn-fg-secondary': '#5e5142', '--pn-accent': '#c0883e', '--pn-accent-dim': 'rgba(192,136,62,0.16)' }, palette: ['#c0883e', '#b45309', '#0d9488', '#dc2626', '#7c3aed', '#2563eb', '#16a34a', '#db2777'] },
};
const THEME_KEYS = Object.keys(THEMES);

// Theme token used as the "current color" preview for each per-widget color field.
const FIELD_THEME_TOKEN = { bg: '--pn-glass', fg: '--pn-fg-primary', accent: '--pn-accent', headerFg: '--pn-fg-quaternary' };

// Split a single theme palette into pie/bar/line sets (lines rotated so they
// read distinctly from the bars in projection charts). Falls back to the
// module palettes for the Default theme (palette === null).
function palettesFor(palette) {
  if (!palette || !palette.length) return { pie: PIE_PALETTE, bar: BAR_PALETTE, line: LINE_PALETTE };
  const half = Math.ceil(palette.length / 2);
  return { pie: palette, bar: palette, line: palette.slice(half).concat(palette.slice(0, half)) };
}

function TemplateRenderBase({ setPageNum, mergedData, messageApi, withContext, initLayout, dashboardNameFromParent, entitySelected, initCanvas, initTheme }) {
    const [editMode, setEditMode] = useState(false);
    const [dashboardName, setDashboardName] = useState('')
    const [editingId, setEditingId] = useState(null);
    const [theme, setTheme] = useState(initTheme && THEMES[initTheme] ? initTheme : 'default');
    const [canvasSize, setCanvasSize] = useState(() => ({
        width: (initCanvas && initCanvas.width) || CANVAS_DEFAULTS.width,
        height: (initCanvas && initCanvas.height) || CANVAS_DEFAULTS.height,
    }));
    // Measure the actual width available to the canvas (accounts for the sidebar,
    // page padding, and browser zoom) so we can warn viewers precisely when the
    // fixed-width dashboard is wider than what fits — they should zoom out.
    const wrapRef = useRef(null);
    const [availW, setAvailW] = useState(Infinity);
    const [warnDismissed, setWarnDismissed] = useState(false);
    useEffect(() => {
        const el = wrapRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const update = () => setAvailW(el.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Dynamically initialize positions for all widgets
    const [widgetStates, setWidgetStates] = useState(() => {
        // Standalone (shared link): use the saved layout, but coerce any chart
        // saved with 'auto'/missing size to a definite default so it can render
        // responsively (ResponsiveContainer needs a sized parent).
        if (!withContext) {
            const norm = { ...initLayout };
            mergedData.forEach((metric) => {
                const ws = { ...(norm[metric.id] || {}) };
                if (CHART_TYPES.includes(metric.data_type)) {
                    const d = defaultSizeFor(metric.data_type);
                    if (ws.width == null || ws.width === 'auto') ws.width = d.width;
                    if (ws.height == null || ws.height === 'auto') ws.height = d.height;
                }
                if (metric.chartStyle && ws.chartStyle == null) ws.chartStyle = metric.chartStyle;
                norm[metric.id] = ws;
            });
            return norm;
        }
        const initialPositions = {};
        mergedData.forEach((metric, index) => {
            const d = defaultSizeFor(metric.data_type);
            initialPositions[metric.id] = {
                x: 20 + (index % 3) * 320,
                y: 20 + Math.floor(index / 3) * 240,
                width: d.width,
                height: d.height,
                ...(metric.chartStyle ? { chartStyle: metric.chartStyle } : {}),
            };
        });
        return initialPositions;
    });

    const handleDragStop = (metricId, d) => {
        setWidgetStates((prev) => ({
            ...prev,
            [metricId]: {
                ...prev[metricId],
                x: d.x,
                y: d.y,
            },
        }));
    };

    const updateWidget = (metricId, patch) => {
        setWidgetStates((prev) => ({
            ...prev,
            [metricId]: { ...prev[metricId], ...patch },
        }));
    };

    const saveDashboardEntity = async () => {
        messageApi.loading("Saving dashboard entity to dashboard...", 0);
        try {
            await saveDashboardEntityToServer({
                layout: widgetStates, data: mergedData,
                info: {
                    'name': dashboardName,
                    'entity_name': entitySelected.name,
                    'canvasWidth': canvasSize.width,
                    'canvasHeight': canvasSize.height,
                    'theme': theme
                }
            });
            messageApi.destroy();
            messageApi.success("Dashboard entity saved");
        } catch (err) {
            messageApi.destroy();
            messageApi.error("Failed to dashboard entity");
            console.error(err);
        }
    }

    const editingMetric = editingId ? mergedData.find((m) => m.id === editingId) : null;
    const tooWide = canvasSize.width > availW + 1;

    return (
        <div ref={wrapRef} className={"pn-render " + (withContext ? "pn-render--app" : "pn-render--standalone")} style={{ width: '100%' }}>
            {tooWide && !warnDismissed && (
                <div className="pn-render-warn" role="alert">
                    <WarningOutlined className="ic" />
                    <span>This dashboard is <b>{canvasSize.width}px</b> wide — wider than your screen. Zoom out (Ctrl/⌘ and −) to see the whole layout.</span>
                    <button type="button" className="x" title="Dismiss" onClick={() => setWarnDismissed(true)}><CloseOutlined /></button>
                </div>
            )}
            <header className="pn-render-bar" style={{width: canvasSize.width, marginLeft: 'auto', marginRight: 'auto'}}>
                {withContext && <>
                    <button type="button" className="pn-btn pn-btn-ghost" onClick={() => setPageNum(4)}><ArrowLeftOutlined /> Back</button>
                    <div className="pn-input-wrap pn-render-name">
                        <input className="pn-control" onChange={(event) => setDashboardName(event.target.value)} value={dashboardName} placeholder="Dashboard name…" type="text" />
                    </div>
                    <div className="pn-render-actions">
                        {editMode && (
                            <label className="pn-theme-pick" title="Dashboard theme">
                                <span>Theme</span>
                                <select className="pn-control" value={theme} onChange={(e) => setTheme(e.target.value)}>
                                    {THEME_KEYS.map((k) => <option key={k} value={k}>{THEMES[k].name}</option>)}
                                </select>
                            </label>
                        )}
                        {editMode && (
                            <CanvasSizeControl value={canvasSize} min={CANVAS_MIN}
                                onCommit={(patch) => setCanvasSize((s) => ({ ...s, ...patch }))} />
                        )}
                        <label className="pn-edit-toggle">
                            <span>Edit mode</span>
                            <Switch checked={editMode} onChange={() => setEditMode(!editMode)} />
                        </label>
                        <button type="button" className="pn-btn pn-btn-accent" onClick={saveDashboardEntity}>
                            <SaveOutlined /> Save to Dashboard
                        </button>
                    </div>
                </>}
                {!withContext && <div className="pn-render-title">{dashboardNameFromParent}</div>}
            </header>
            <div className={"pn-render-canvas dashboard-maker" + (editMode ? " editing" : "")} style={{ ...(THEMES[theme] ? THEMES[theme].vars : {}), width: canvasSize.width }}>
                <div className="pn-render-surface" style={{ position: 'relative', width: canvasSize.width, height: canvasSize.height }}>
                    {mergedData.map((metric) => (
                        <Rnd
                            key={metric.id}
                            size={{ width: widgetStates[metric.id].width, height: widgetStates[metric.id].height }}
                            position={{ x: widgetStates[metric.id].x, y: widgetStates[metric.id].y }}
                            onDragStop={(e, d) => handleDragStop(metric.id, d)}
                            disableDragging={!editMode}
                            bounds="parent"
                            enableResizing={false}
                            style={{
                                boxSizing: 'border-box',
                                border: editMode ? '1.5px dashed var(--pn-accent)' : '1px solid transparent',
                                borderRadius: 'var(--pn-radius-card, 14px)',
                                transition: 'border-color 0.2s',
                            }}
                        >
                            <div className="pn-widget" style={{ width: '100%', height: '100%', position: 'relative', cursor: editMode ? 'move' : 'default' }}>
                                {editMode && withContext && (
                                    <button type="button" className="pn-widget-edit" title="Edit widget"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); setEditingId(metric.id); }}>
                                        <EditOutlined />
                                    </button>
                                )}
                                <RenderMetric metric={metric} custom={widgetStates[metric.id]} palette={THEMES[theme] ? THEMES[theme].palette : null} />
                            </div>
                        </Rnd>
                    ))}
                </div>
            </div>

            {editingMetric && (
                <WidgetEditModal
                    metric={editingMetric}
                    value={widgetStates[editingId]}
                    onChange={(patch) => updateWidget(editingId, patch)}
                    onClose={() => setEditingId(null)}
                    themeVars={THEMES[theme] ? THEMES[theme].vars : {}}
                    palette={THEMES[theme] ? THEMES[theme].palette : null}
                />
            )}
        </div>
    )
}

/* Canvas width/height control: lets the user clear and type freely, then
   validates/clamps on blur or Enter (so they can fully delete "480" first). */
function CanvasSizeControl({ value, min, onCommit }) {
    const [w, setW] = useState(String(value.width));
    const [h, setH] = useState(String(value.height));
    useEffect(() => { setW(String(value.width)); }, [value.width]);
    useEffect(() => { setH(String(value.height)); }, [value.height]);

    const commit = (raw, key) => {
        const n = Math.max(min[key], Math.round(Number(raw)) || min[key]);
        onCommit({ [key]: n });
        if (key === 'width') setW(String(n)); else setH(String(n));
    };

    return (
        <div className="pn-canvas-size" title="Canvas size (px) — the dashboard is saved at this fixed width">
            <span>Canvas</span>
            <input type="number" className="pn-control" value={w} min={min.width}
                onChange={(e) => setW(e.target.value)} onBlur={() => commit(w, 'width')}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
            <span className="x">×</span>
            <input type="number" className="pn-control" value={h} min={min.height}
                onChange={(e) => setH(e.target.value)} onBlur={() => commit(h, 'height')}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
        </div>
    );
}

/* ------------------------------ edit modal -------------------------------- */
function FieldControl({ name, value, onChange, themeVars = {} }) {
    const meta = FIELD_META[name];
    const v = value[name];
    if (meta.kind === 'color') {
        // When there's no per-widget override, preview the effective theme color
        // (the real value, alpha and all) rather than a static default.
        const themed = themeVars[FIELD_THEME_TOKEN[name]];
        const swatch = v || themed || meta.def;
        return (
            <div className="pn-field">
                <label>{meta.label}</label>
                <div className="pn-color-row">
                    <ColorPicker value={swatch} onChange={(c) => onChange({ [name]: c.toRgbString() })} defaultFormat="rgb" />
                    <span className="pn-color-val">{v || 'Theme default'}</span>
                    <button type="button" className="pn-btn pn-btn-ghost pn-btn-sm" onClick={() => onChange({ [name]: '' })}>Reset</button>
                </div>
            </div>
        );
    }
    if (meta.kind === 'number') {
        return (
            <div className="pn-field">
                <label>{meta.label}</label>
                <div className="pn-input-wrap">
                    <input type="number" className="pn-control" placeholder={meta.placeholder || 'default'} value={v ?? ''}
                        onChange={(e) => onChange({ [name]: e.target.value === '' ? '' : Number(e.target.value) })} />
                </div>
            </div>
        );
    }
    if (meta.kind === 'switch') {
        return (
            <div className="pn-field pn-field-row">
                <label>{meta.label}</label>
                <Switch checked={v !== false} onChange={(c) => onChange({ [name]: c })} />
            </div>
        );
    }
    if (meta.kind === 'select') {
        return (
            <div className="pn-field">
                <label>{meta.label}</label>
                <div className="pn-input-wrap">
                    <select className="pn-control" value={v ?? meta.def}
                        onChange={(e) => onChange({ [name]: e.target.value })}>
                        {meta.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
            </div>
        );
    }
    return null;
}

function WidgetEditModal({ metric, value, onChange, onClose, themeVars = {}, palette }) {
    const fields = fieldsFor(metric.data_type);
    const seriesFields = seriesColorFields(metric);
    const defaults = defaultColorMap(metric, palette);
    const setColor = (key, c) => onChange({ colors: { ...(value.colors || {}), [key]: c } });
    return (
        <Modal title={`Customize · ${metric.name}`} open onCancel={onClose} onOk={onClose}
            okText="Done" cancelButtonProps={{ style: { display: 'none' } }} width={540} style={{ top: 40 }}>
            <div className="pn-modal-embed">
                <div className="pn-render-edit-fields">
                    {fields.map((f) => <FieldControl key={f} name={f} value={value} onChange={onChange} themeVars={themeVars} />)}
                </div>
                {seriesFields.length > 0 && (
                    <>
                        <div className="pn-fieldset-title" style={{ marginTop: 20 }}>Series colors</div>
                        <div className="pn-render-edit-fields">
                            {seriesFields.map((s) => (
                                <div className="pn-field" key={s.key}>
                                    <label title={s.label}>{s.label}</label>
                                    <div className="pn-color-row">
                                        <ColorPicker
                                            value={(value.colors && value.colors[s.key]) || defaults[s.key] || '#3ec6c0'}
                                            onChange={(c) => setColor(s.key, c.toRgbString())} defaultFormat="rgb" />
                                        <span className="pn-color-val">{(value.colors && value.colors[s.key]) || 'Auto'}</span>
                                        <button type="button" className="pn-btn pn-btn-ghost pn-btn-sm" onClick={() => setColor(s.key, '')}>Reset</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}

function RenderMetric({ metric, custom = {}, palette }) {
    switch (metric.data_type) {
        case ("Big Number Card"): {
            return <BigNumberCard data={metric.data} name={metric.name} custom={custom} />
        }
        case ("Pie Chart"): {
            return <PieChart data={metric.data} name={metric.name} custom={custom} palette={palette} />
        }
        case ("Waterfall Chart"): {
            return <WaterfallChart data={metric.data} name={metric.name} custom={custom} />
        }
        case ("Chart (Projections)"): {
            return <ProjectionsChart data={metric.data} name={metric.name} custom={custom} palette={palette} />
        }
        case ("Two Col Table"): {
            return <RenderOneTable
                tableStyles={{ margin: '0px', height: '100%' }}
                styles={tableContainerStyle(custom)}
                data={metric.data} name={metric.name} />
        }
        case ("More Than Two Col Table"): {
            return <RenderProjectionTable
                tableStyles={{ margin: '0px', height: '100%' }}
                styles={tableContainerStyle(custom)}
                data={metric.data} name={metric.name} />
        }
        case ("Cap Table"): {
            return <CapTable
                tableStyles={{ margin: '0px', height: '100%' }}
                styles={tableContainerStyle(custom)}
                data={metric.data} />
        }
        default: {
            return <pre>{JSON.stringify(metric, null, 2)}</pre>
        }
    }
}

function BigNumberCard({ data, name, custom = {} }) {
    // Extract percentage value if it's a currency string
    const displayValue = data;
    const numericValue = typeof data === 'string' ? parseFloat(data.replace(/[^0-9.-]/g, '')) : data;
    const percentage = Math.min(Math.abs(numericValue), 100);
    const isPercentage = typeof data === 'string' && data.includes('%');
    const accent = col(custom.accent, 'var(--pn-accent)');
    const ringSize = Number(custom.ringSize) > 0 ? Number(custom.ringSize) : 80;
    const fg = col(custom.fg, 'var(--pn-fg-primary)');

    return (
        <div className="pn-render-bignum" style={{
            background: col(custom.bg, 'var(--pn-glass)'),
            border: '1px solid var(--pn-border-default)',
            borderRadius: px(custom.radius, 'var(--pn-radius-card, 14px)'),
            padding: '16px 18px',
            color: fg,
            minHeight: '120px',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.3s ease, border-color 0.2s ease'
        }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Header: accent chip + label (mirrors .pn-kpi .kh) */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    fontSize: px(custom.titleSize, '12px'), fontWeight: '500',
                    color: col(custom.fg, 'var(--pn-fg-tertiary)')
                }}>
                    <span style={{
                        width: '26px', height: '26px', borderRadius: '7px', flex: 'none',
                        display: 'grid', placeItems: 'center',
                        background: 'var(--pn-accent-dim)', color: accent, fontSize: '14px'
                    }}>
                        {cardIconFor(name)}
                    </span>
                    {name}
                </div>

                {/* Value (mirrors .pn-kpi .kv) */}
                <div style={{
                    fontSize: px(custom.valueSize, '27px'),
                    fontWeight: '600',
                    letterSpacing: '-0.6px',
                    marginTop: '12px',
                    fontVariantNumeric: 'tabular-nums',
                    color: fg
                }}>
                    {displayValue}
                </div>

                {/* Footer (mirrors .pn-kpi .kd.up) */}
                {custom.showTag !== false && (
                    <div style={{
                        fontSize: '12px', marginTop: '5px',
                        color: 'var(--pn-success)'
                    }}>
                        firm-wide
                    </div>
                )}
            </div>

            {isPercentage && (
                <div style={{ position: 'relative', width: ringSize, height: ringSize * (70 / 120), flexShrink: 0 }}>
                    <svg width={ringSize} height={ringSize * (70 / 120)} viewBox="0 0 120 70">
                        {/* Background arc (semi circle) */}
                        <path
                            d="M 10 60 A 50 50 0 0 1 110 60"
                            fill="none"
                            stroke="var(--pn-tint-08)"
                            strokeWidth="16"
                            strokeLinecap="round"
                            pathLength="100"
                        />
                        {/* Progress arc (semi circle) */}
                        <path
                            d="M 10 60 A 50 50 0 0 1 110 60"
                            fill="none"
                            stroke={accent}
                            strokeWidth="16"
                            strokeLinecap="round"
                            pathLength="100"
                            strokeDasharray={`${percentage} 100`}
                            style={{ transition: 'stroke-dasharray 0.6s ease' }}
                        />
                    </svg>
                </div>
            )}
        </div>
    );
}

function PieChart({ data, name, custom = {}, palette }) {
    // data is an array of { label: string, value: number (0-1 or percentage) }
    const customColors = custom.colors || {};
    const pie = palettesFor(palette).pie;

    // Convert values to percentages if needed
    const normalizedData = data.map(item => ({
        ...item,
        value: item.value <= 1 ? item.value : item.value / 100
    }));

    // Calculate pie slices
    let currentAngle = 0;
    const slices = normalizedData.map((item, index) => {
        const percentage = item.value;
        const angle = percentage * 360;
        const slice = {
            ...item,
            startAngle: currentAngle,
            endAngle: currentAngle + angle,
            percentage: percentage * 100,
            color: customColors[item.label] || pie[index % pie.length]
        };
        currentAngle += angle;
        return slice;
    });

    // Create SVG path for pie slice
    const createArc = (startAngle, endAngle, radius = 100) => {
        const start = polarToCartesian(120, 120, radius, endAngle);
        const end = polarToCartesian(120, 120, radius, startAngle);
        const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
        return `M 120 120 L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
    };

    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
        const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
        return {
            x: centerX + radius * Math.cos(angleInRadians),
            y: centerY + radius * Math.sin(angleInRadians)
        };
    };

    const holeColor = col(custom.bg, 'var(--pn-bg-panel)');
    const pieSize = Number(custom.chartSize) > 0 ? Number(custom.chartSize) : null;
    const pieBoxStyle = pieSize
        ? { position: 'relative', width: pieSize, height: pieSize, flex: 'none' }
        : { position: 'relative', flex: '0 0 38%', maxWidth: '170px', minWidth: '90px', aspectRatio: '1 / 1' };

    return (
        <div style={{
            background: col(custom.bg, 'var(--pn-glass)'),
            border: '1px solid var(--pn-border-default)',
            borderRadius: px(custom.radius, 'var(--pn-radius-panel, 14px)'),
            padding: '18px 20px 16px',
            color: col(custom.fg, 'var(--pn-fg-primary)'),
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '32px',
            overflow: 'auto'
        }}>
            <div style={pieBoxStyle}>
                <svg width="100%" height="100%" viewBox="0 0 240 240" style={{ display: 'block' }}>
                    {slices.map((slice, index) => {
                        // A full-circle slice (≈100%) can't be drawn with a single SVG
                        // arc — both endpoints land on the same coordinate and the path
                        // collapses. Render a plain circle instead.
                        const isFullCircle = slice.endAngle - slice.startAngle >= 359.999;
                        return (
                        <AntTooltip
                            key={index}
                            title={`${slice.label}: ${slice.percentage.toFixed(1)}%`}
                        >
                            {isFullCircle ? (
                                <circle
                                    cx="120"
                                    cy="120"
                                    r="100"
                                    fill={slice.color}
                                    style={{
                                        transition: 'transform 0.3s ease',
                                        transformOrigin: '120px 120px',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                />
                            ) : (
                            <path
                                d={createArc(slice.startAngle, slice.endAngle)}
                                fill={slice.color}
                                style={{
                                    transition: 'transform 0.3s ease',
                                    transformOrigin: '120px 120px',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            />
                            )}
                        </AntTooltip>
                        );
                    })}
                    {/* Center hole */}
                    <circle cx="120" cy="120" r="60" fill={holeColor} />
                </svg>
            </div>

            <div style={{ flex: 1 }}>
                <h3 style={{
                    margin: '0 0 16px 0',
                    fontSize: px(custom.titleSize, '15px'),
                    fontWeight: '600',
                    letterSpacing: '-0.2px',
                    color: col(custom.fg, 'var(--pn-fg-primary)')
                }}>
                    {name}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {slices.map((slice, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '5px 0'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '7px' }}>
                                <div style={{
                                    width: '11px',
                                    height: '11px',
                                    borderRadius: '3px',
                                    backgroundColor: slice.color
                                }} />
                                <span style={{
                                    fontSize: px(custom.fontSize, '11px'),
                                    color: col(custom.fg, 'var(--pn-fg-secondary)'),
                                    fontWeight: '500'
                                }}>
                                    {slice.label}
                                </span>
                            </div>
                            <span style={{
                                fontSize: px(custom.fontSize, '11px'),
                                fontWeight: '600',
                                color: col(custom.fg, 'var(--pn-fg-primary)')
                            }}>
                                {slice.percentage.toFixed(1)}%
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function WaterfallChart({ data, name, custom = {} }) {
    // Parse numeric values from strings
    const parseValue = (val) => {
        if (val.includes("$")) val = val.replace("$", "");
        if (typeof val === 'number') return val;
        return parseFloat(val.replace(/,/g, '')) || 0;
    };

    // Determine chart type and calculate cumulative values
    const chartData = data.map((item, index) => {
        const numericValue = parseValue(item.value);
        const label = item.label;

        // First and last items are totals
        const isTotal = index === 0 || index === data.length - 1;

        // Determine if it's a positive or negative change based on label keywords
        const isNegative = label.toLowerCase().includes('churn') ||
            label.toLowerCase().includes('contraction') ||
            label.toLowerCase().includes('decrease');

        return {
            label,
            value: numericValue,
            displayValue: item.value,
            isTotal,
            isNegative: !isTotal && isNegative
        };
    });

    // Calculate cumulative positions
    let cumulative = 0;
    const bars = chartData.map((item, index) => {
        if (item.isTotal) {
            const bar = {
                ...item,
                start: 0,
                end: item.value,
                cumulative: item.value
            };
            cumulative = item.value;
            return bar;
        } else {
            const change = item.isNegative ? -item.value : item.value;
            const start = cumulative;
            cumulative += change;
            return {
                ...item,
                start: Math.min(start, cumulative),
                end: Math.max(start, cumulative),
                cumulative,
                change
            };
        }
    });

    const maxValue = Math.max(...bars.map(b => Math.max(b.end, b.start)));
    const chartHeight = 168;
    const barWidth = Number(custom.barSize) > 0 ? Number(custom.barSize) : 30;
    const gap = 40;
    const chartWidth = bars.length * (barWidth + gap);

    const getY = (value) => {
        return chartHeight - (value / maxValue) * (chartHeight - 40);
    };

    const textColor = col(custom.fg, 'var(--pn-fg-primary)');
    const subColor = col(custom.fg, 'var(--pn-fg-tertiary)');
    const wfColors = custom.colors || {};

    return (
        <div style={{
            background: col(custom.bg, 'var(--pn-glass)'),
            border: '1px solid var(--pn-border-default)',
            borderRadius: px(custom.radius, 'var(--pn-radius-panel, 14px)'),
            padding: '18px 20px 16px',
            color: textColor,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <h3 style={{
                margin: '0 0 16px 0',
                fontSize: px(custom.titleSize, '15px'),
                fontWeight: '600',
                letterSpacing: '-0.2px',
                color: textColor
            }}>
                {name}
            </h3>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center' }}>
            <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight + 80}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block', width: '100%', height: 'auto', maxHeight: '100%' }}>
                {bars.map((bar, index) => {
                    const x = index * (barWidth + gap) + gap / 2;
                    const y1 = getY(bar.start);
                    const y2 = getY(bar.end);
                    const barHeight = Math.abs(y2 - y1);
                    const barY = Math.min(y1, y2);

                    let color;
                    if (bar.isTotal) {
                        color = wfColors.total || 'var(--pn-accent)';
                    } else if (bar.isNegative) {
                        color = wfColors.decrease || '#e74c3c';
                    } else {
                        color = wfColors.increase || '#27ae60';
                    }

                    return (
                        <g key={index}>
                            {/* Connector line to next bar */}
                            {index < bars.length - 1 && !bar.isTotal && (
                                <line
                                    x1={x + barWidth}
                                    y1={getY(bar.cumulative)}
                                    x2={x + barWidth + gap}
                                    y2={getY(bar.cumulative)}
                                    stroke="var(--pn-border-strong)"
                                    strokeWidth="2"
                                    strokeDasharray="4,4"
                                />
                            )}

                            {/* Bar */}
                            <rect
                                x={x}
                                y={barY}
                                width={barWidth}
                                height={Math.max(barHeight, 2)}
                                fill={color}
                                rx="4"
                                style={{
                                    transition: 'opacity 0.3s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                            />

                            {/* Value label on top of bar */}
                            <text
                                x={x + barWidth / 2}
                                y={barY - 8}
                                textAnchor="middle"
                                style={{
                                    fontSize: px(custom.fontSize, '13px'),
                                    fontWeight: '600',
                                    fill: textColor
                                }}
                            >
                                {bar.displayValue}
                            </text>

                            {/* Label below bar */}
                            <text
                                x={x + barWidth / 2}
                                y={chartHeight + 20}
                                textAnchor="middle"
                                style={{
                                    fontSize: px(custom.fontSize, '12px'),
                                    fill: subColor,
                                    fontWeight: '500'
                                }}
                            >
                                {bar.label.split(' ').map((word, i) => (
                                    <tspan key={i} x={x + barWidth / 2} dy={i === 0 ? 0 : 14}>
                                        {word}
                                    </tspan>
                                ))}
                            </text>
                        </g>
                    );
                })}

                {/* Base line */}
                <line
                    x1={0}
                    y1={chartHeight}
                    x2={chartWidth}
                    y2={chartHeight}
                    stroke="var(--pn-border-default)"
                    strokeWidth="2"
                />
            </svg>
            </div>
        </div>
    );
}

function ProjectionsChart({ data, name, custom = {}, palette }) {
    const customColors = custom.colors || {};
    const pal = palettesFor(palette);

    // Parse numeric values
    const parseValue = (val) => {
        if (typeof val === 'number') return val;
        return parseFloat(val.replace(/[$,%]/g, '').replace(/,/g, '')) || 0;
    };

    // Separate data into money (bars) and percentage (lines) series
    const moneySeries = [];
    const percentageSeries = [];

    data.forEach((row, index) => {
        const label = row[0];
        const values = row.slice(1);

        // Check if this is a percentage row
        const isPercentage = values.some(v => String(v).includes('%')) ||
            label.toLowerCase().includes('%') ||
            label.toLowerCase().includes('percent');

        const numericValues = values.map(parseValue);

        if (isPercentage) {
            percentageSeries.push({
                label,
                values,
                numericValues,
                color: customColors[label] || pal.line[percentageSeries.length % pal.line.length]
            });
        } else {
            moneySeries.push({
                label,
                values,
                numericValues,
                color: customColors[label] || pal.bar[moneySeries.length % pal.bar.length]
            });
        }
    });

    // Generate period labels (Year 0, Year 1, etc.)
    const periods = data[0].slice(1).map((_, i) => `Year ${i}`);

    // Build chart data
    const chartData = periods.map((period, i) => {
        const dataPoint = { year: period };

        moneySeries.forEach((serie) => {
            dataPoint[serie.label] = serie.numericValues[i];
        });

        percentageSeries.forEach((serie) => {
            dataPoint[serie.label] = serie.numericValues[i];
        });

        return dataPoint;
    });

    // Pick the money unit (millions vs thousands) from the largest value so
    // small models read "$320k" while large ones read "$9.6M" — matching the
    // dashboard's Financial Projection chart.
    const maxMoney = moneySeries.reduce(
        (m, s) => Math.max(m, ...s.numericValues.map((v) => Math.abs(v))), 0);
    const useMillions = maxMoney >= 1e6;
    const formatCurrency = (value) =>
        useMillions ? `$${(value / 1e6).toFixed(1)}M` : `$${(value / 1e3).toFixed(0)}k`;

    const formatPercentage = (value) => {
        return `${value.toFixed(0)}%`;
    };

    const titleColor = col(custom.fg, 'var(--pn-fg-primary)');
    const subColor = 'var(--pn-fg-tertiary)';
    const tickSize = Number(custom.fontSize) > 0 ? Number(custom.fontSize) : 12;
    const moneyAsLine = (custom.chartStyle || 'Line') === 'Line';
    const subtitle = [...moneySeries, ...percentageSeries].map((s) => s.label).join(' · ');

    return (
        <div style={{
            background: col(custom.bg, 'var(--pn-glass)'),
            borderRadius: px(custom.radius, 'var(--pn-radius-panel, 14px)'),
            border: '1px solid var(--pn-border-default)',
            color: titleColor,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <div style={{ padding: '18px 20px 8px' }}>
                <h3 style={{
                    margin: 0,
                    fontSize: px(custom.titleSize, '15px'),
                    fontWeight: 600,
                    letterSpacing: '-0.2px',
                    color: titleColor,
                }}>
                    {name}
                </h3>
                {subtitle && (
                    <div style={{
                        marginTop: 2,
                        fontSize: tickSize,
                        fontWeight: 500,
                        color: subColor,
                    }}>
                        {subtitle}
                    </div>
                )}
            </div>

            <div style={{ flex: 1, minHeight: 0, paddingBottom: 12 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={chartData}
                    margin={{ top: 28, right: 40, left: 12, bottom: 12 }}
                >
                    <defs>
                        {moneySeries.map((serie, idx) => (
                            <linearGradient key={`barGradient-${idx}`} id={`barGradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={serie.color} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={serie.color} stopOpacity={0.5} />
                            </linearGradient>
                        ))}
                    </defs>

                    <CartesianGrid
                        strokeDasharray="3 4"
                        stroke="var(--pn-border-default)"
                        vertical={false}
                        opacity={0.6}
                    />

                    <XAxis
                        dataKey="year"
                        tick={{ fontSize: tickSize + 1, fill: 'var(--pn-fg-tertiary)', fontWeight: 500 }}
                        axisLine={{ stroke: 'var(--pn-border-strong)', strokeWidth: 1.5 }}
                        tickLine={false}
                    />

                    {/* Left Y-axis for money values */}
                    <YAxis
                        yAxisId="left"
                        tick={{ fontSize: tickSize, fill: 'var(--pn-fg-tertiary)' }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatCurrency}
                    />

                    {/* Right Y-axis for percentage values */}
                    {percentageSeries.length > 0 && (
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: tickSize, fill: '#a78bfa' }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={formatPercentage}
                        />
                    )}

                    <Tooltip
                        contentStyle={{
                            backgroundColor: "rgba(15, 23, 42, 0.95)",
                            border: "1px solid rgba(148, 163, 184, 0.2)",
                            borderRadius: "12px",
                            fontSize: "13px",
                            padding: "12px 16px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)"
                        }}
                        labelStyle={{ color: '#e2e8f0', fontWeight: 600, marginBottom: '8px' }}
                        itemStyle={{ color: '#e2e8f0', padding: '4px 0' }}
                        formatter={(value, name) => {
                            const isPercentage = percentageSeries.some(s => s.label === name);
                            const formatted = isPercentage ? formatPercentage(value) : formatCurrency(value);
                            return [formatted, name];
                        }}
                    />

                    <Legend
                        verticalAlign="top"
                        align="left"
                        wrapperStyle={{
                            fontSize: tickSize + 1,
                            paddingBottom: "16px",
                            color: 'var(--pn-fg-secondary)'
                        }}
                        height={32}
                        iconType="line"
                    />

                    {/* Render money values as bars or lines depending on the chosen style */}
                    {moneySeries.map((serie, idx) => (
                        moneyAsLine ? (
                            <Line
                                key={`money-line-${idx}`}
                                yAxisId="left"
                                type="monotone"
                                dataKey={serie.label}
                                stroke={serie.color}
                                strokeWidth={2.5}
                                dot={{
                                    fill: col(custom.bg, '#141b24'),
                                    stroke: serie.color,
                                    strokeWidth: 2,
                                    r: 4
                                }}
                                activeDot={{
                                    r: 6,
                                    filter: `drop-shadow(0 0 8px ${serie.color}60)`
                                }}
                                isAnimationActive={true}
                                animationDuration={800}
                            >
                                <LabelList
                                    dataKey={serie.label}
                                    position="top"
                                    formatter={formatCurrency}
                                    style={{ fill: serie.color, fontSize: tickSize - 1, fontWeight: 600 }}
                                />
                            </Line>
                        ) : (
                            <Bar
                                key={`bar-${idx}`}
                                yAxisId="left"
                                dataKey={serie.label}
                                fill={`url(#barGradient-${idx})`}
                                radius={[3, 3, 0, 0]}
                                isAnimationActive={true}
                                animationDuration={800}
                            />
                        )
                    ))}

                    {/* Render lines for percentage values */}
                    {percentageSeries.map((serie, idx) => (
                        <Line
                            key={`line-${idx}`}
                            yAxisId="right"
                            type="monotone"
                            dataKey={serie.label}
                            stroke={serie.color}
                            strokeWidth={2.5}
                            dot={{
                                fill: col(custom.bg, '#141b24'),
                                stroke: serie.color,
                                strokeWidth: 2,
                                r: 4
                            }}
                            activeDot={{
                                r: 6,
                                filter: `drop-shadow(0 0 8px ${serie.color}60)`
                            }}
                            isAnimationActive={true}
                            animationDuration={800}
                        />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
            </div>
        </div>
    );
}
